/**
 * SystemHealthTab — "Système" admin tab.
 *
 * Permanent home for what used to be drawer-only diagnostics
 * (`SystemHealthDrawer`), plus a live-metrics card added after the
 * Sep 2026 load test: confirms Redis-backed WS/rate-limiting are
 * actually active and shows how many live WS connections this pod is
 * holding, so the multi-pod fix is self-verifiable from the UI
 * instead of only via `/api/system/health` curl calls.
 */
import React, { useEffect, useState, useCallback } from "react";
import { RefreshCcw, CheckCircle2, AlertTriangle, XCircle, Circle, Activity, Radio, Server } from "lucide-react";
import { useSystemHealth } from "../../lib/useSystemHealth";
import { adminGetLiveMetrics } from "../../lib/api";

const STATUS_META = {
  ok:       { color: "emerald", Icon: CheckCircle2, label: "OK" },
  degraded: { color: "amber",   Icon: AlertTriangle, label: "DÉGRADÉ" },
  down:     { color: "rose",    Icon: XCircle,       label: "PANNE" },
  disabled: { color: "zinc",    Icon: Circle,        label: "DÉSACTIVÉ" },
  unknown:  { color: "zinc",    Icon: Circle,        label: "INCONNU" },
};

const COLOR_CLASSES = {
  emerald: { border: "border-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-300" },
  amber:   { border: "border-amber-500/50",   bg: "bg-amber-500/10",   text: "text-amber-200" },
  rose:    { border: "border-rose-500/60",    bg: "bg-rose-500/15",    text: "text-rose-200" },
  zinc:    { border: "border-zinc-700",       bg: "bg-zinc-800/40",    text: "text-zinc-400" },
};

function DependencyCard({ depKey, dep }) {
  const meta = STATUS_META[dep.status] || STATUS_META.unknown;
  const c = COLOR_CLASSES[meta.color];
  const { Icon } = meta;
  const extras = Object.entries(dep).filter(([k]) => !["status", "label", "detail", "latency_ms"].includes(k));
  return (
    <div data-testid={`sys-dep-${depKey}`} className={`border ${c.border} ${c.bg} p-3 flex flex-col gap-1.5`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 ${c.text} shrink-0`} strokeWidth={2.5} />
          <span className="font-mono text-[11px] font-bold tracking-wider text-zinc-100 truncate">{dep.label}</span>
        </div>
        <span data-testid={`sys-dep-${depKey}-status`} className={`font-mono text-[9px] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5 border ${c.border} ${c.text}`}>
          {meta.label}
        </span>
      </div>
      <div className="text-[11px] text-zinc-300 leading-snug pl-6 break-words">{dep.detail || "—"}</div>
      {(extras.length > 0 || dep.latency_ms != null) && (
        <div className="pl-6 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono text-zinc-500 tabular-nums">
          {dep.latency_ms != null && <span>⟳ {dep.latency_ms} ms</span>}
          {extras.map(([k, v]) => (
            <span key={k}><span className="text-zinc-600">{k}=</span><span className="text-zinc-400">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></span>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveMetricsCard({ metrics, loading, error }) {
  const podReady = metrics?.multi_pod_ready;
  const c = podReady ? COLOR_CLASSES.emerald : COLOR_CLASSES.amber;
  return (
    <div data-testid="sys-live-metrics" className={`border ${c.border} ${c.bg} p-4 flex flex-col gap-3`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Radio className={`w-4 h-4 ${c.text}`} strokeWidth={2.5} />
          <span className="font-mono text-xs font-bold tracking-wider text-zinc-100">Capacité multi-pod (charge)</span>
        </div>
        <span data-testid="sys-multi-pod-badge" className={`font-mono text-[9px] font-bold tracking-[0.18em] uppercase px-1.5 py-0.5 border ${c.border} ${c.text}`}>
          {podReady ? "PRÊT" : "MONO-POD"}
        </span>
      </div>
      {error && <div className="text-[11px] text-rose-300 font-mono">{error}</div>}
      {metrics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
          <MetricBox label="WS pub/sub" value={metrics.ws_pubsub_mode} />
          <MetricBox label="Rate-limit" value={metrics.rate_limit_mode} />
          <MetricBox label="Admins connectés (ce pod)" value={metrics.local_connections?.admin_firehose ?? "—"} />
          <MetricBox label="Suivis d'alerte (ce pod)" value={metrics.local_connections?.per_alert_tracking ?? "—"} />
        </div>
      )}
      {metrics && (
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-zinc-500 pt-1 border-t border-zinc-800">
          <Server className="w-3 h-3" /> {metrics.pod_id}
        </div>
      )}
      <p className="text-[10px] text-zinc-500 leading-relaxed">
        {podReady
          ? "Redis actif : ces compteurs sont propres à ce pod, mais les diffusions WS et les limites de débit sont partagées entre tous les pods."
          : "Redis non actif : chaque pod fonctionne isolément (WS et rate-limit non partagés). Voir REDIS_URL dans .env."}
      </p>
    </div>
  );
}

function MetricBox({ label, value }) {
  return (
    <div className="border border-zinc-800 bg-black/30 py-2 px-1">
      <div className="font-mono text-lg font-bold text-zinc-100 tabular-nums">{String(value)}</div>
      <div className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

export const SystemHealthTab = () => {
  const { data, loading, error, refresh } = useSystemHealth({ intervalMs: 15_000 });
  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);

  const fetchMetrics = useCallback(async () => {
    try {
      const m = await adminGetLiveMetrics();
      setMetrics(m);
      setMetricsError(null);
    } catch (e) {
      setMetricsError(e?.response?.data?.detail || "Sonde des métriques impossible.");
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchMetrics();
    }, 15_000);
    return () => clearInterval(id);
  }, [fetchMetrics]);

  const overall = data?.overall || "unknown";
  const meta = STATUS_META[overall] || STATUS_META.unknown;
  const c = COLOR_CLASSES[meta.color];
  const deps = data?.dependencies || {};
  const depKeys = ["db", "redis", "storage", "sms", "email", "push"];

  return (
    <div data-testid="admin-system-tab" className="p-4 sm:p-6 space-y-5 max-w-4xl">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          <Activity className={`w-5 h-5 ${c.text}`} strokeWidth={2.5} />
          <div>
            <div className="text-[9px] font-bold tracking-[0.3em] uppercase text-zinc-500">Diagnostic système</div>
            <div className={`font-mono text-sm font-bold tracking-wider ${c.text}`} data-testid="sys-overall">
              {overall === "ok" && "TOUS SYSTÈMES OPÉRATIONNELS"}
              {overall === "degraded" && "DÉGRADATION DÉTECTÉE"}
              {overall === "down" && "PANNE EN COURS"}
              {overall === "unknown" && "SONDE EN COURS…"}
            </div>
          </div>
        </div>
        <button
          type="button"
          data-testid="sys-refresh-btn"
          onClick={() => { refresh(); fetchMetrics(); }}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 disabled:opacity-50 text-[11px] font-mono uppercase tracking-wider"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Re-sonder
        </button>
      </div>

      <LiveMetricsCard metrics={metrics} loading={metricsLoading} error={metricsError} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {depKeys.map((k) => (deps[k] ? <DependencyCard key={k} depKey={k} dep={deps[k]} /> : null))}
      </div>
    </div>
  );
};

export default SystemHealthTab;
